import moment from "moment-timezone";
export const convertTo24HourFormat = (unformattedTime) => {
  if (!unformattedTime) {
    return unformattedTime;
  }

  const [time, period] = unformattedTime.split(" ");
  const [hours, minutes] = time.split(":");
  let hour24 = parseInt(hours);

  if (period === "PM" && hour24 !== 12) {
    hour24 += 12;
  } else if (period === "AM" && hour24 === 12) {
    hour24 = 0;
  }
  return `${hour24.toString().padStart(2, "0")}:${minutes}`;
};

export const convertTo12HourFormat = (time24) => {
  if (!time24) {
    return time24;
  }

  const [hours, minutes] = time24.split(":");
  const hour = parseInt(hours);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;

  return `${hour12}:${minutes} ${period}`;
};

export const formatDateTime = (
  date,
  timezone = "UTC",
  format = "M/D/YY h:mm A",
) => {
  if (!date) return "-";
  return moment
    .utc(date)
    .tz(timezone || "UTC")
    .format(format);
};

// Default IANA timezone used when a salon's timezone is missing/blank.
export const DEFAULT_TIMEZONE = "America/New_York";

/**
 * Resolve the salon's IANA timezone from the SalonContext `businessInfo`,
 * falling back to {@link DEFAULT_TIMEZONE} when it is missing/blank.
 *
 * Single source of truth for the salon timezone so scheduling code does not
 * inline inconsistent fallbacks (some paths historically defaulted to
 * "America/New_York", others to "UTC", producing wrong instants when a salon's
 * timezone was empty). Use this everywhere a salon-local wall-clock is
 * converted to/from an absolute instant.
 *
 * @param {{ timezone?: string | null } | null | undefined} businessInfo
 * @returns {string} A valid IANA timezone id.
 */
export const getSalonTimezone = (businessInfo) =>
  (businessInfo && businessInfo.timezone) || DEFAULT_TIMEZONE;

/**
 * Parse an API datetime into a salon-local moment. API datetimes (start_dt,
 * calc__end_dt, suggested-time slots, ...) are UTC instants, so they are read
 * as UTC and THEN converted: `moment.utc(s).tz(tz)`. Never `moment.tz(s, tz)`,
 * which reads the UTC string as salon wall-clock and shifts it by the offset.
 *
 * @param {string} apiDateTime - UTC datetime string from the API
 * @param {string} timeZone - Salon IANA timezone
 * @returns {import("moment-timezone").Moment}
 */
export const parseApiDateTime = (apiDateTime, timeZone) =>
  moment.utc(apiDateTime).tz(timeZone);

/**
 * Normalize a Mantine date value to a "YYYY-MM-DD" calendar-day string.
 *
 * Mantine v8 `DateInput` emits a "YYYY-MM-DD" string, but our forms may still
 * hold a `Date` (initial value / edit prefill), so accept both. A string is
 * taken literally (its date portion) — never via `new Date(str)`, which parses
 * as UTC midnight and rolls back a day when read with local getters in
 * negative-offset zones. A `Date` is read with local getters (the day the user
 * saw in the picker).
 *
 * @param {Date|string} value - The selected day.
 * @returns {string} "YYYY-MM-DD", or "" when the value can't be interpreted.
 */
export const toCalendarDay = (value) => {
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return "";
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(value.getDate()).padStart(2, "0")}`;
  }
  if (typeof value === "string") {
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(value);
    return match ? match[1] : "";
  }
  return "";
};

/**
 * Combine a selected calendar day with a wall-clock time and interpret the
 * result in the salon's timezone, returning an ISO 8601 string (with offset).
 *
 * Accepts the day as either a `Date` or a Mantine "YYYY-MM-DD" string (see
 * `toCalendarDay`). The day is combined with `time` and parsed directly in the
 * salon tz, so the stored instant matches the salon's wall-clock and never rolls
 * across timezones (e.g. browser NY, salon LA, near midnight).
 *
 * @param {Date|string} dateObj - The selected day (Mantine DateInput value).
 * @param {string} time - Wall-clock time as "HH:mm".
 * @param {string} timeZone - IANA salon timezone (falls back to DEFAULT_TIMEZONE).
 * @returns {string} ISO 8601 datetime string, or "" when inputs are invalid.
 */
export const salonWallClockToISO = (dateObj, time, timeZone) => {
  const zone =
    typeof timeZone === "string" && timeZone.trim().length > 0
      ? timeZone.trim()
      : DEFAULT_TIMEZONE;

  const datePart = toCalendarDay(dateObj);
  if (!datePart) return "";

  const timeM = moment(time, "HH:mm");
  if (!timeM.isValid()) return "";

  return moment
    .tz(`${datePart} ${timeM.format("HH:mm")}`, "YYYY-MM-DD HH:mm", zone)
    .seconds(0)
    .milliseconds(0)
    .format();
};

/**
 * Cross-field check mirroring the backend rule that a recurring time block's
 * recurrence end date must be after the block's own end instant (end_dt). The
 * backend parses recurrence_end_date at midnight and rejects when that instant
 * is <= end_dt ("recurrence_end_date must be after end_dt"). Because the
 * recurrence date is at midnight, the rule reduces to a strict calendar-day
 * comparison (DST-immune): the recurrence end day must be after the block's end
 * day. Same-day fails, since midnight of that day precedes the block's end time.
 *
 * Returns a field-keyed errors partial ({} when valid) for the manual setErrors
 * pattern the time-block forms use. Presence of the dates is handled by the
 * forms' required-field checks, so missing/unparseable input returns {} here.
 *
 * @param {Date|string} recurrenceEndDate - Recurrence end day (Date or "YYYY-MM-DD").
 * @param {Date|string} endDate - The block's end day (Date or "YYYY-MM-DD").
 * @returns {{recurrenceEndDate?: string}} Errors partial ({} when valid).
 */
export const validateRecurrenceEndAfterBlockEnd = (
  recurrenceEndDate,
  endDate,
) => {
  const recurrenceDay = toCalendarDay(recurrenceEndDate);
  const endDay = toCalendarDay(endDate);
  if (!recurrenceDay || !endDay) return {};
  // "YYYY-MM-DD" strings compare lexically == chronologically.
  if (recurrenceDay > endDay) return {};
  return {
    recurrenceEndDate:
      "Recurrence end date must be after the time block's end date",
  };
};

/**
 * Convert a react-big-calendar "fake-local" Date — one whose browser-local
 * wall-clock deliberately equals the salon's wall-clock (see
 * transactionUtils.mapBookedServiceToEvent) — into a true UTC ISO string for
 * that salon wall-clock. The local components are reinterpreted in the salon
 * tz, so a drag/resize by an operator whose browser tz differs from the salon's
 * still persists the time the operator actually saw on the grid. A plain
 * `date.toISOString()` would instead encode the wall-clock as the operator's
 * browser tz and shift the stored time. No-op when browser tz === salon tz.
 *
 * @param {Date} date - Fake-local Date from RBC (onEventDrop / onEventResize).
 * @param {string} timeZone - IANA salon timezone (falls back to DEFAULT_TIMEZONE).
 * @returns {string} UTC ISO 8601 string (Z form), or "" when the date is invalid.
 */
export const fakeLocalDateToSalonISO = (date, timeZone) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return "";
  const zone =
    typeof timeZone === "string" && timeZone.trim().length > 0
      ? timeZone.trim()
      : DEFAULT_TIMEZONE;
  return moment
    .tz(moment(date).format("YYYY-MM-DDTHH:mm:ss"), zone)
    .toISOString();
};

export const calculateAge = (birthday) => {
  if (!birthday) return null;

  const today = new Date();
  const birthDate = new Date(birthday);

  // Check if birthday is valid
  if (isNaN(birthDate.getTime())) return null;

  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  // If birthday hasn't occurred yet this year, subtract one year
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--;
  }

  return age;
};

/**
 * Get date range presets for reports filtering
 * @param {string} timezone - Optional timezone for date calculations (e.g., "America/New_York")
 * @param {boolean} includePayPeriod - Whether to include "This Pay Period" option (default: false)
 * @returns {Array} Array of date range preset objects with label, value, startDate, and endDate
 */
export const getDateRangePresets = (
  timezone = null,
  includePayPeriod = false,
) => {
  // Use timezone-aware moment if timezone is provided, otherwise use browser's local time
  const today = timezone ? moment.tz(timezone) : moment();

  const presets = [
    {
      label: "Last 30 Days",
      value: "last_30_days",
      startDate: today.clone().subtract(30, "days").startOf("day").toDate(),
      endDate: today.clone().endOf("day").toDate(),
    },
    {
      label: "Today",
      value: "today",
      startDate: today.clone().startOf("day").toDate(),
      endDate: today.clone().endOf("day").toDate(),
    },
    {
      label: "This Week",
      value: "this_week",
      startDate: today.clone().startOf("week").toDate(),
      endDate: today.clone().endOf("week").toDate(),
    },
    {
      label: "Last Week",
      value: "last_week",
      startDate: today.clone().subtract(1, "week").startOf("week").toDate(),
      endDate: today.clone().subtract(1, "week").endOf("week").toDate(),
    },
    {
      label: "This Month",
      value: "this_month",
      startDate: today.clone().startOf("month").toDate(),
      endDate: today.clone().endOf("month").toDate(),
    },
    {
      label: "Last Month",
      value: "last_month",
      startDate: today.clone().subtract(1, "month").startOf("month").toDate(),
      endDate: today.clone().subtract(1, "month").endOf("month").toDate(),
    },
    {
      label: "Year to Date",
      value: "year_to_date",
      startDate: today.clone().startOf("year").toDate(),
      endDate: today.clone().endOf("day").toDate(),
    },
    {
      label: "Last Year",
      value: "last_year",
      startDate: today.clone().subtract(1, "year").startOf("year").toDate(),
      endDate: today.clone().subtract(1, "year").endOf("year").toDate(),
    },
    {
      label: "Custom Range",
      value: "custom",
      startDate: today.clone().subtract(30, "days").startOf("day").toDate(),
      endDate: today.clone().endOf("day").toDate(),
    },
  ];

  // Only include "This Pay Period" if includePayPeriod is true
  if (includePayPeriod) {
    // Insert before "Custom Range"
    presets.splice(presets.length - 1, 0, {
      label: "This Pay Period",
      value: "this_pay_period",
      // Initial dates will be replaced by API call when selected
      startDate: today.clone().startOf("day").toDate(),
      endDate: today.clone().endOf("day").toDate(),
    });
  }

  return presets;
};

/**
 * Calculate total duration for a service or booked service
 * Sums base_duration_mins, processing_time_mins, finishing_time_mins, and buffer_time_mins
 * @param {Object} service - Service object (SalonService, StaffService, or BookedService)
 * @returns {number} Total duration in minutes
 */
export const calculateTotalDuration = (service) => {
  if (!service) return 0;

  const baseDuration =
    service.base_duration_mins ?? service.default_base_duration_mins ?? 0;
  const processingTime =
    service.processing_time_mins ?? service.default_processing_time_mins ?? 0;
  const finishingTime =
    service.finishing_time_mins ?? service.default_finishing_time_mins ?? 0;
  const bufferTime =
    service.buffer_time_mins ?? service.default_buffer_time_mins ?? 0;

  return baseDuration + processingTime + finishingTime + bufferTime;
};

/**
 * Compact duration label from a minute count: "1h 30m", "45m", "2h".
 * Negative/NaN inputs clamp to 0 ("0m").
 * @param {number} mins - Duration in minutes
 * @returns {string}
 */
export const formatDurationShort = (mins) => {
  const total = Math.max(0, Math.round(Number(mins) || 0));
  const h = Math.floor(total / 60);
  const m = total % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
};
